import { API_BASE } from '@/shared/services/api';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, Clock, MapPin, ChevronRight, Filter,
  CheckCircle, AlertCircle, Navigation, Phone, MessageSquare, Loader2
} from 'lucide-react';
import { MobileButton, MobileCard, MobileBadge } from '../components/MobileUIKit';
import { MobileBottomNav } from '../components/MobileBottomNav';
import '../styles/mobile.css';

const API_URL = API_BASE;

const statusConfig = {
  ready: { label: 'READY', color: 'var(--mobile-success)', bg: 'rgba(76, 175, 80, 0.2)' },
  pending: { label: 'PENDING', color: 'var(--mobile-warning)', bg: 'rgba(255, 167, 38, 0.2)' },
  in_progress: { label: 'IN PROGRESS', color: 'var(--mobile-info)', bg: 'rgba(66, 165, 245, 0.2)' },
  completed: { label: 'COMPLETED', color: 'var(--mobile-text-tertiary)', bg: 'var(--mobile-bg-tertiary)' },
  in_transit: { label: 'IN TRANSIT', color: 'var(--mobile-info)', bg: 'rgba(66, 165, 245, 0.2)' },
  delivered: { label: 'DELIVERED', color: 'var(--mobile-success)', bg: 'rgba(76, 175, 80, 0.2)' },
};

export const DriverOrdersPage = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('ezerpToken');
        const response = await fetch(`${API_URL}/api/ezparcel/driver/orders`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          // Transform API data to match expected format
          const transformedOrders = data.map(order => ({
            id: order.id || order.tracking_number,
            status: order.status?.toLowerCase() || 'pending',
            pickup: { 
              name: 'Warehouse', 
              address: 'Distribution Center', 
              distance: '1.0 mi' 
            },
            dropoff: { 
              name: order.customer_name || 'Customer', 
              address: order.destination_address || 'Address', 
              distance: '3.0 mi' 
            },
            earnings: order.cod_amount || 0,
            tip: 0,
            items: 1,
            estimatedTime: '15 min',
            specialInstructions: null,
          }));
          setOrders(transformedOrders);
        }
      } catch (err) {
        console.error('Failed to fetch orders:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  const filteredOrders = filter === 'all' 
    ? orders 
    : orders.filter(o => o.status === filter);

  const handleOrderClick = (order) => {
    if (order.status === 'ready' || order.status === 'in_progress') {
      navigate('/driver/route');
    }
  };

  if (loading) {
    return (
      <div className="mobile-app min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--ezp-primary)' }} />
      </div>
    );
  }

  return (
    <div className="mobile-app min-h-screen pb-20">
      {/* Header */}
      <div className="mobile-safe-top mobile-px-4 pt-4 pb-3">
        <div className="mobile-flex mobile-justify-between mobile-items-center">
          <h1 className="mobile-text-title" style={{ color: 'var(--mobile-text-primary)' }}>
            Orders
          </h1>
          <button 
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: 'var(--mobile-bg-tertiary)' }}
          >
            <Filter className="w-5 h-5" style={{ color: 'var(--mobile-text-secondary)' }} />
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="mobile-px-4 mb-4">
        <div 
          className="mobile-flex mobile-gap-2 p-1 rounded-xl"
          style={{ background: 'var(--mobile-bg-secondary)' }}
        >
          {[
            { key: 'all', label: 'All' },
            { key: 'ready', label: 'Ready' },
            { key: 'in_progress', label: 'Active' },
            { key: 'pending', label: 'Pending' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`
                flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all
              `}
              style={{
                background: filter === tab.key ? 'var(--ezp-primary)' : 'transparent',
                color: filter === tab.key ? 'white' : 'var(--mobile-text-secondary)',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Orders Count */}
      <div className="mobile-px-4 mb-3">
        <p className="mobile-text-caption">
          {filteredOrders.length} order{filteredOrders.length !== 1 ? 's' : ''} found
        </p>
      </div>

      {/* Orders List */}
      <div className="mobile-px-4">
        {filteredOrders.map(order => {
          const status = statusConfig[order.status];
          
          return (
            <MobileCard 
              key={order.id} 
              elevated 
              interactive
              className="mb-3 mobile-animate-fade-in"
              onClick={() => handleOrderClick(order)}
            >
              {/* Order Header */}
              <div className="mobile-flex mobile-justify-between mobile-items-start mb-3">
                <div>
                  <p 
                    className="font-semibold"
                    style={{ color: 'var(--mobile-text-primary)' }}
                  >
                    {order.id}
                  </p>
                  <p className="mobile-text-tiny mt-1">{order.items} items</p>
                </div>
                <span
                  className="px-2 py-1 rounded-full text-xs font-bold"
                  style={{ background: status.bg, color: status.color }}
                >
                  {status.label}
                </span>
              </div>

              {/* Route Visualization */}
              <div className="mobile-flex mobile-items-center mobile-gap-2 mb-3">
                <div className="flex flex-col items-center">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ background: 'var(--mobile-info)' }}
                  />
                  <div 
                    className="w-0.5 h-8"
                    style={{ background: 'var(--mobile-border)' }}
                  />
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ background: 'var(--mobile-success)' }}
                  />
                </div>
                <div className="flex-1">
                  <div className="mb-3">
                    <p 
                      className="font-medium text-sm"
                      style={{ color: 'var(--mobile-text-primary)' }}
                    >
                      {order.pickup.name}
                    </p>
                    <p className="mobile-text-tiny">{order.pickup.address}</p>
                  </div>
                  <div>
                    <p 
                      className="font-medium text-sm"
                      style={{ color: 'var(--mobile-text-primary)' }}
                    >
                      {order.dropoff.name}
                    </p>
                    <p className="mobile-text-tiny">{order.dropoff.address}</p>
                  </div>
                </div>
              </div>

              {/* Order Footer */}
              <div 
                className="mobile-flex mobile-justify-between mobile-items-center pt-3 border-t"
                style={{ borderColor: 'var(--mobile-divider)' }}
              >
                <div className="mobile-flex mobile-items-center mobile-gap-3">
                  <div className="mobile-flex mobile-items-center mobile-gap-1">
                    <Clock className="w-4 h-4" style={{ color: 'var(--mobile-text-tertiary)' }} />
                    <span className="mobile-text-tiny">{order.estimatedTime}</span>
                  </div>
                  <div className="mobile-flex mobile-items-center mobile-gap-1">
                    <Navigation className="w-4 h-4" style={{ color: 'var(--mobile-text-tertiary)' }} />
                    <span className="mobile-text-tiny">{order.dropoff.distance}</span>
                  </div>
                </div>
                <div className="text-right">
                  <p 
                    className="font-bold"
                    style={{ color: 'var(--mobile-earnings)' }}
                  >
                    ${(order.earnings ?? 0).toFixed(2)}
                  </p>
                  {order.tip > 0 && (
                    <p className="mobile-text-tiny" style={{ color: 'var(--mobile-tip)' }}>
                      incl. ${(order.tip ?? 0).toFixed(2)} tip
                    </p>
                  )}
                </div>
              </div>

              {/* Special Instructions */}
              {order.specialInstructions && (
                <div 
                  className="mt-3 p-2 rounded-lg mobile-flex mobile-items-center mobile-gap-2"
                  style={{ background: 'var(--mobile-bg-tertiary)' }}
                >
                  <AlertCircle className="w-4 h-4" style={{ color: 'var(--mobile-warning)' }} />
                  <p className="mobile-text-tiny" style={{ color: 'var(--mobile-text-secondary)' }}>
                    {order.specialInstructions}
                  </p>
                </div>
              )}

              {/* Quick Actions */}
              {order.status === 'in_progress' && (
                <div className="mobile-flex mobile-gap-2 mt-3">
                  <button 
                    className="flex-1 mobile-btn mobile-btn-secondary py-2"
                    onClick={(e) => { e.stopPropagation(); }}
                  >
                    <Phone className="w-4 h-4 mr-1" />
                    Call
                  </button>
                  <button 
                    className="flex-1 mobile-btn mobile-btn-secondary py-2"
                    onClick={(e) => { e.stopPropagation(); }}
                  >
                    <MessageSquare className="w-4 h-4 mr-1" />
                    Chat
                  </button>
                  <button 
                    className="flex-1 mobile-btn mobile-btn-primary py-2"
                    onClick={(e) => { e.stopPropagation(); navigate('/driver/route'); }}
                  >
                    <Navigation className="w-4 h-4 mr-1" />
                    Navigate
                  </button>
                </div>
              )}
            </MobileCard>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredOrders.length === 0 && (
        <div className="mobile-px-4 text-center py-12">
          <Package 
            className="w-16 h-16 mx-auto mb-4" 
            style={{ color: 'var(--mobile-text-disabled)' }}
          />
          <p className="mobile-text-subtitle" style={{ color: 'var(--mobile-text-primary)' }}>
            No orders found
          </p>
          <p className="mobile-text-caption mt-2">
            {filter === 'all' 
              ? 'Go online to start receiving orders'
              : 'Try a different filter'}
          </p>
        </div>
      )}

      {/* Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
};

export default DriverOrdersPage;
